import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangle, 
  ArrowRight, 
  FileText,
  Receipt,
  Calculator,
  CircleDollarSign
} from 'lucide-react';
import { listVoucherEntries } from '@/app/services/voucherService';
import { VoucherEntryResponse } from '@/app/types/voucher';
import { cn } from '@/lib/utils';

interface VoucherEntriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  voucherId: number;
  voucherNumber?: string;
}

const formatCurrency = (amount: string): string => {
  const numAmount = parseFloat(amount);
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount);
};

const getEntryTypeIcon = (entryType: string) => {
  switch (entryType) {
    case 'main':
      return <FileText className="h-4 w-4" />;
    case 'tax':
      return <Receipt className="h-4 w-4" />;
    case 'discount':
      return <CircleDollarSign className="h-4 w-4" />;
    case 'rounding':
      return <Calculator className="h-4 w-4" />;
    default:
      return <FileText className="h-4 w-4" />;
  }
};

const getEntryTypeBadgeVariant = (entryType: string) => {
  switch (entryType) {
    case 'main':
      return 'default';
    case 'tax':
      return 'secondary';
    case 'discount':
      return 'outline';
    case 'rounding':
      return 'outline';
    default:
      return 'default';
  }
};

export const VoucherEntriesModal: React.FC<VoucherEntriesModalProps> = ({
  isOpen,
  onClose,
  voucherId,
  voucherNumber,
}) => {
  const { data: entries, isLoading, isError, error } = useQuery<VoucherEntryResponse[], Error>({
    queryKey: ['voucher-entries', voucherId],
    queryFn: () => listVoucherEntries(voucherId),
    enabled: isOpen && !!voucherId,
  });

  // Group entries by entry type
  const groupedEntries = React.useMemo(() => {
    if (!entries) return {};
    
    return entries.reduce((acc, entry) => {
      if (!acc[entry.entry_type]) {
        acc[entry.entry_type] = [];
      }
      acc[entry.entry_type].push(entry);
      return acc;
    }, {} as Record<string, VoucherEntryResponse[]>);
  }, [entries]);

  // Calculate totals
  const totals = React.useMemo(() => {
    if (!entries) return { debit: 0, credit: 0 };
    
    return entries.reduce((acc, entry) => {
      acc.debit += parseFloat(entry.debit_amount);
      acc.credit += parseFloat(entry.credit_amount);
      return acc;
    }, { debit: 0, credit: 0 });
  }, [entries]);

  // Get transaction flow entries (FROM and TO)
  const flowEntries = React.useMemo(() => {
    if (!entries) return { from: [], to: [] };
    
    return {
      from: entries.filter(e => e.is_from_account),
      to: entries.filter(e => e.is_to_account),
    };
  }, [entries]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Voucher Entries {voucherNumber && `- ${voucherNumber}`}
          </DialogTitle>
          <DialogDescription>
            Detailed accounting entries showing the transaction flow
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        )}

        {isError && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Error loading voucher entries: {error?.message || 'Unknown error occurred'}
            </AlertDescription>
          </Alert>
        )}

        {entries && entries.length > 0 && (
          <div className="space-y-6">
            {/* Transaction Flow Visualization */}
            {(flowEntries.from.length > 0 || flowEntries.to.length > 0) && (
              <div className="bg-muted/50 rounded-lg p-4">
                <h3 className="text-sm font-semibold mb-3">Transaction Flow</h3>
                <div className="flex items-center justify-between gap-4">
                  {/* FROM Accounts */}
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-2">FROM (Credit)</p>
                    <div className="space-y-2">
                      {flowEntries.from.map((entry) => (
                        <div key={entry.id} className="bg-background rounded p-2 text-sm">
                          <p className="font-medium">{entry.account_name}</p>
                          <p className="text-muted-foreground text-xs">{formatCurrency(entry.credit_amount)}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Arrow */}
                  <ArrowRight className="h-6 w-6 text-muted-foreground flex-shrink-0" />

                  {/* TO Accounts */}
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-2">TO (Debit)</p>
                    <div className="space-y-2">
                      {flowEntries.to.map((entry) => (
                        <div key={entry.id} className="bg-background rounded p-2 text-sm">
                          <p className="font-medium">{entry.account_name}</p>
                          <p className="text-muted-foreground text-xs">{formatCurrency(entry.debit_amount)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Detailed Entries Table */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Detailed Entries</h3>
              <div className="rounded-md border">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="p-2 text-left text-xs font-medium">Account</th>
                      <th className="p-2 text-left text-xs font-medium">Type</th>
                      <th className="p-2 text-right text-xs font-medium">Debit</th>
                      <th className="p-2 text-right text-xs font-medium">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(groupedEntries).map(([entryType, typeEntries]) => (
                      <React.Fragment key={entryType}>
                        {typeEntries.map((entry, index) => (
                          <tr key={entry.id} className={cn(
                            "border-b",
                            index === typeEntries.length - 1 && "border-b-2"
                          )}>
                            <td className="p-2">
                              <div>
                                <p className="font-medium text-sm">{entry.account_name}</p>
                                {entry.account_code && (
                                  <p className="text-xs text-muted-foreground">{entry.account_code}</p>
                                )}
                                {entry.account_group_name && (
                                  <p className="text-xs text-muted-foreground">{entry.account_group_name}</p>
                                )}
                              </div>
                            </td>
                            <td className="p-2">
                              <Badge variant={getEntryTypeBadgeVariant(entry.entry_type) as any} className="gap-1">
                                {getEntryTypeIcon(entry.entry_type)}
                                <span className="capitalize">{entry.entry_type}</span>
                              </Badge>
                            </td>
                            <td className="p-2 text-right text-sm">
                              {parseFloat(entry.debit_amount) > 0 ? formatCurrency(entry.debit_amount) : '-'}
                            </td>
                            <td className="p-2 text-right text-sm">
                              {parseFloat(entry.credit_amount) > 0 ? formatCurrency(entry.credit_amount) : '-'}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                    {/* Totals Row */}
                    <tr className="bg-muted/50 font-semibold">
                      <td colSpan={2} className="p-2 text-right">Total</td>
                      <td className="p-2 text-right">{formatCurrency(totals.debit.toString())}</td>
                      <td className="p-2 text-right">{formatCurrency(totals.credit.toString())}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              {/* Balance Check */}
              {Math.abs(totals.debit - totals.credit) > 0.01 && (
                <Alert variant="destructive" className="mt-2">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Warning: Debits and credits do not balance. Difference: {formatCurrency(Math.abs(totals.debit - totals.credit).toString())}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        )}

        {entries && entries.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No entries found for this voucher.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};